// Static diagrams for the Xinu docs page. Each figure renders one SVG with
// role="img" so tests and screen readers can find it by its accessible name.
const INK = 'currentColor';

function Box({ x, y, w, h, children, dashed = false }) {
    return (
        <g>
            <rect x={x} y={y} width={w} height={h} rx={6} fill="none" stroke={INK} strokeWidth={1.5} strokeDasharray={dashed ? '5 4' : undefined} />
            <text x={x + w / 2} y={y + h / 2} textAnchor="middle" dominantBaseline="central" fontSize={13} fontFamily="ui-monospace, monospace" fill={INK}>
                {children}
            </text>
        </g>
    );
}

function DownArrow({ x, y1, y2, label }) {
    return (
        <g stroke={INK} strokeWidth={1.5} fill="none">
            <line x1={x} y1={y1} x2={x} y2={y2} />
            <polyline points={`${x - 5},${y2 - 7} ${x},${y2} ${x + 5},${y2 - 7}`} />
            {label && (
                <text x={x + 10} y={(y1 + y2) / 2} dominantBaseline="central" fontSize={12} fill={INK} stroke="none">
                    {label}
                </text>
            )}
        </g>
    );
}

function RightArrow({ x1, x2, y, label }) {
    return (
        <g stroke={INK} strokeWidth={1.5} fill="none">
            <line x1={x1} y1={y} x2={x2} y2={y} />
            <polyline points={`${x2 - 7},${y - 5} ${x2},${y} ${x2 - 7},${y + 5}`} />
            {label && (
                <text x={(x1 + x2) / 2} y={y - 8} textAnchor="middle" fontSize={12} fill={INK} stroke="none">
                    {label}
                </text>
            )}
        </g>
    );
}

export function BootDiagram() {
    const steps = ['startup.S', 'nulluser()', 'sysinit()', 'enable()', 'create(main)', 'ready(MAIN)', 'null thread idles'];
    const cx = 260;
    const bw = 220;
    const bh = 34;
    const gap = 22;
    return (
        <svg viewBox="0 0 520 460" role="img" aria-label="Boot sequence diagram" className="xinu-figure-svg">
            {steps.map((step, i) => {
                const y = 10 + i * (bh + gap);
                return (
                    <g key={step}>
                        <Box x={cx - bw / 2} y={y} w={bw} h={bh} dashed={i === steps.length - 1}>{step}</Box>
                        {i < steps.length - 1 && <DownArrow x={cx} y1={y + bh} y2={y + bh + gap} />}
                    </g>
                );
            })}
        </svg>
    );
}

export function ThreadStateDiagram() {
    return (
        <svg viewBox="0 0 520 330" role="img" aria-label="Thread state diagram" className="xinu-figure-svg">
            <Box x={10} y={20} w={100} h={36}>THRFREE</Box>
            <Box x={210} y={20} w={100} h={36}>THRSUSP</Box>
            <Box x={410} y={20} w={100} h={36}>THRREADY</Box>
            <Box x={410} y={140} w={100} h={36}>THRCURR</Box>
            <Box x={210} y={140} w={100} h={36}>THRRECV</Box>
            <Box x={10} y={140} w={100} h={36}>THRSLEEP</Box>
            <RightArrow x1={112} x2={206} y={38} label="create" />
            <RightArrow x1={312} x2={406} y={38} label="ready" />
            <DownArrow x={460} y1={58} y2={136} />
            <text x={472} y={100} fontSize={12} fill={INK}>resched</text>
            <g stroke={INK} strokeWidth={1.5} fill="none">
                <line x1={410} y1={158} x2={312} y2={158} />
                <polyline points="322,153 312,158 322,163" />
            </g>
            <text x={361} y={150} textAnchor="middle" fontSize={12} fill={INK}>receive</text>
            <g stroke={INK} strokeWidth={1.5} fill="none">
                <line x1={260} y1={140} x2={260} y2={58} />
                <polyline points="255,68 260,58 265,68" />
            </g>
            <text x={272} y={100} fontSize={12} fill={INK}>send + ready</text>
            <RightArrow x1={112} x2={206} y={158} label="wakeup" />
            <g stroke={INK} strokeWidth={1.5} fill="none">
                <line x1={410} y1={130} x2={150} y2={130} strokeDasharray="5 4" />
                <line x1={150} y1={130} x2={150} y2={158} strokeDasharray="5 4" />
                <polyline points="145,148 150,158 155,148" />
            </g>
            <text x={280} y={122} textAnchor="middle" fontSize={12} fill={INK}>sleep</text>
            <text x={460} y={200} textAnchor="middle" fontSize={12} fill={INK}>running</text>
            <text x={260} y={200} textAnchor="middle" fontSize={12} fill={INK}>waiting for message</text>
        </svg>
    );
}

export function MessageDiagram() {
    return (
        <svg viewBox="0 0 520 300" role="img" aria-label="Message send and receive diagram" className="xinu-figure-svg">
            <Box x={10} y={30} w={150} h={36}>send(tid, msg)</Box>
            <Box x={185} y={30} w={150} h={36}>thrtab[tid]</Box>
            <Box x={360} y={30} w={150} h={36}>receive()</Box>
            <RightArrow x1={162} x2={181} y={48} />
            <RightArrow x1={337} x2={356} y={48} />
            <text x={260} y={100} textAnchor="middle" fontSize={12} fill={INK}>msg = msg; hasmsg = TRUE;</text>
            <text x={260} y={120} textAnchor="middle" fontSize={12} fill={INK}>receiver in THRRECV → ready(tid)</text>
            <text x={260} y={160} textAnchor="middle" fontSize={12} fill={INK}>no message → state = THRRECV, resched()</text>
            <text x={260} y={180} textAnchor="middle" fontSize={12} fill={INK}>msg = thr-&gt;msg; hasmsg = FALSE;</text>
            <Box x={185} y={220} w={150} h={36} dashed>one slot per thread</Box>
        </svg>
    );
}

export function ReadyQueueDiagram() {
    const entries = [['MAIN', '16'], ['T1', '20'], ['T2', '12']];
    return (
        <svg viewBox="0 0 520 260" role="img" aria-label="Ready queue diagram" className="xinu-figure-svg">
            <text x={20} y={30} fontSize={13} fontFamily="ui-monospace, monospace" fill={INK}>readylist (highest priority first)</text>
            {entries.map(([name, prio], i) => (
                <g key={name}>
                    <rect x={20 + i * 160} y={50} width={140} height={60} rx={6} fill="none" stroke={INK} strokeWidth={1.5} />
                    <text x={90 + i * 160} y={74} textAnchor="middle" fontSize={13} fontFamily="ui-monospace, monospace" fill={INK}>{name}</text>
                    <text x={90 + i * 160} y={94} textAnchor="middle" fontSize={12} fill={INK}>prio {prio}</text>
                    {i < entries.length - 1 && <RightArrow x1={162 + i * 160} x2={178 + i * 160} y={80} />}
                </g>
            ))}
            <text x={20} y={150} fontSize={12} fill={INK}>resched(): if current prio &gt; firstkey(readylist), keep running;</text>
            <text x={20} y={170} fontSize={12} fill={INK}>otherwise dequeue the head and ctxsw() to it.</text>
            <text x={20} y={205} fontSize={12} fill={INK}>clkhandler() fires 1000 times per second and calls resched().</text>
        </svg>
    );
}

export function MemoryDiagram() {
    return (
        <svg viewBox="0 0 520 260" role="img" aria-label="Free memory list diagram" className="xinu-figure-svg">
            <text x={20} y={30} fontSize={13} fontFamily="ui-monospace, monospace" fill={INK}>memlist (address order)</text>
            <Box x={20} y={50} w={90} h={44}>head</Box>
            <Box x={130} y={50} w={110} h={44}>len 512</Box>
            <Box x={260} y={50} w={110} h={44}>len 2048</Box>
            <Box x={390} y={50} w={90} h={44}>NULL</Box>
            <RightArrow x1={112} x2={126} y={72} />
            <RightArrow x1={242} x2={256} y={72} />
            <RightArrow x1={372} x2={386} y={72} />
            <text x={20} y={135} fontSize={12} fill={INK}>memget(): exact fit → unlink; larger block → split off the front.</text>
            <text x={20} y={155} fontSize={12} fill={INK}>memfree(): insert in address order, merge with adjacent blocks.</text>
            <text x={20} y={190} fontSize={12} fill={INK}>All sizes rounded up to a memblock multiple (roundmb).</text>
        </svg>
    );
}
